import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Minus, Plus, ChevronLeft, Truck, Shield, Check, Star, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedVariations, setSelectedVariations] = useState({});
    const [selectedImage, setSelectedImage] = useState(0);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await axios.get(`${API}/products/${productId}`);
                setProduct(response.data);
                
                // Initialize default variations
                const defaults = {};
                response.data.variations?.forEach(v => {
                    if (v.options?.length > 0) {
                        defaults[v.name] = v.options[0];
                    }
                });
                setSelectedVariations(defaults);

                // Fetch related products from the same category
                if (response.data.category_id) {
                    const relatedRes = await axios.get(`${API}/products`, {
                        params: { category: response.data.category_id, limit: 5 }
                    });
                    // Filter out the current product
                    const filtered = relatedRes.data.products.filter(p => p.product_id !== productId);
                    setRelatedProducts(filtered.slice(0, 4));
                }
            } catch (error) {
                console.error('Error fetching product:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
        // Scroll to top when product changes
        window.scrollTo(0, 0);
    }, [productId]);

    const handleAddToCart = async () => {
        const success = await addToCart(product.product_id, quantity, selectedVariations);
        if (success) {
            toast.success('Продуктът е добавен в количката');
        } else {
            toast.error('Грешка при добавяне в количката');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="container-main py-8">
                    <div className="animate-pulse">
                        <div className="h-8 w-32 bg-muted rounded mb-8" />
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div className="aspect-square bg-muted rounded-lg" />
                            <div className="space-y-4">
                                <div className="h-8 bg-muted rounded w-3/4" />
                                <div className="h-6 bg-muted rounded w-1/4" />
                                <div className="h-32 bg-muted rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h1 className="font-serif text-2xl font-bold mb-4 text-[#6B1028]">Продуктът не е намерен</h1>
                    <Button onClick={() => navigate('/products')} className="btn-primary">
                        Обратно към продуктите
                    </Button>
                </div>
            </div>
        );
    }

    const hasDiscount = product.discount_percent && product.discount_percent > 0;
    const discountedPrice = hasDiscount 
        ? product.price * (1 - product.discount_percent / 100)
        : product.price;

    // Filter out empty/invalid images
    const allImages = [product.image_url, ...(product.images || [])].filter(img => img && img.length > 0);
    const hasImages = allImages.length > 0 && allImages[0];

    return (
        <div className="min-h-screen bg-background">
            <div className="container-main py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-[#6B1028]/70 hover:text-[#6B1028] mb-8 transition-colors font-medium"
                    data-testid="back-btn"
                >
                    <ChevronLeft className="h-5 w-5 mr-1" />
                    Назад
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Images */}
                    <div className="space-y-4">
                        <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-600 border-2 border-amber-200 dark:border-slate-600 shadow-sm">
                            {hasImages ? (
                                <img 
                                    src={allImages[selectedImage] || allImages[0]} 
                                    alt={product.name}
                                    className="w-full h-full object-contain p-4"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling?.classList.remove('hidden');
                                    }}
                                />
                            ) : null}
                            <div className={`w-full h-full flex items-center justify-center ${hasImages ? 'hidden' : ''}`}>
                                <Package className="w-32 h-32 text-amber-300" />
                            </div>
                        </div>
                        
                        {allImages.length > 1 && (
                            <div className="flex gap-3">
                                {allImages.map((img, idx) => (
                                    <button
                                        key={`img-${idx}-${img.slice(-20)}`}
                                        onClick={() => setSelectedImage(idx)}
                                        className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-600 ${
                                            selectedImage === idx ? 'border-amber-500 shadow-md' : 'border-amber-200 dark:border-slate-600 hover:border-amber-400'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-contain p-1" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Details */}
                    <div>
                        {/* Brand */}
                        {product.brand && (
                            <p className="text-sm text-amber-600 uppercase tracking-[0.15em] font-bold mb-2">
                                {product.brand}
                            </p>
                        )}

                        {/* Name */}
                        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2 text-[#6B1028]" data-testid="product-name">
                            {product.name}
                        </h1>
                        
                        {/* Weight/Volume — shown only when admin confirms it's correct */}
                        {product.weight_visible && product.weight && (
                            <p className="text-base text-blue-600 font-semibold mb-4 flex items-center gap-2">
                                <Package className="h-5 w-5" />
                                {product.weight} {product.weight_unit || 'гр'}
                            </p>
                        )}

                        {/* Price */}
                        <div className="flex flex-col gap-1 mb-6">
                            <div className="flex items-center gap-4">
                                <span className="text-3xl font-bold text-amber-600" data-testid="product-price">
                                    {getPriceData(discountedPrice).eurFormatted} €
                                </span>
                                {hasDiscount && (
                                    <>
                                        <span className="text-xl text-[#6B1028]/50 line-through">
                                            {getPriceData(product.price).eurFormatted} €
                                        </span>
                                        <span className="bg-[#6B1028] text-white px-3 py-1 text-sm font-bold rounded-md">
                                            -{product.discount_percent}%
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Quantity tier pricing */}
                        {product.promo?.tiers?.length > 0 && (
                            <div className="mb-6 border-2 border-amber-300 rounded-xl overflow-hidden" data-testid="quantity-tiers-box">
                                <div className="bg-amber-100 dark:bg-amber-900/40 px-4 py-2 flex items-center gap-2">
                                    <span className="text-sm font-bold text-amber-800 dark:text-amber-300">🏷 Купи повече и спести</span>
                                    <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-semibold">КОЛИЧЕСТВО ОФЕРТА</span>
                                </div>
                                <div className="divide-y divide-amber-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                                    <div className="flex justify-between px-4 py-2 text-sm">
                                        <span>1 бр.</span>
                                        <span className="font-semibold">{getPriceData(product.promo.regular_price).eurFormatted} €</span>
                                    </div>
                                    {product.promo.tiers.map((tier, i) => (
                                        <div key={i} className="flex justify-between px-4 py-2 text-sm" data-testid={`tier-row-${tier.min_qty}`}>
                                            <span>{tier.min_qty}+ бр.</span>
                                            <span className="font-bold text-red-600">{getPriceData(tier.unit_price).eurFormatted} € / бр.</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Stock */}
                        <div className="flex items-center gap-2 mb-6">
                            {product.stock > 0 ? (
                                <>
                                    <Check className="h-5 w-5 text-green-600" />
                                    <span className="text-green-600 font-semibold">В наличност</span>
                                    <span className="text-[#6B1028]/60">({product.stock} бр.)</span>
                                </>
                            ) : (
                                <span className="text-[#6B1028] font-semibold">Изчерпано</span>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-[#6B1028]/70 mb-8 leading-relaxed">
                            {product.description}
                        </p>

                        {/* Variations */}
                        {product.variations?.length > 0 && (
                            <div className="space-y-4 mb-8">
                                {product.variations.map((variation) => (
                                    <div key={variation.name}>
                                        <label className="block font-semibold mb-2 text-[#6B1028]">{variation.name}</label>
                                        <div className="flex flex-wrap gap-2">
                                            {variation.options.map((option) => (
                                                <button
                                                    key={option}
                                                    onClick={() => setSelectedVariations({
                                                        ...selectedVariations,
                                                        [variation.name]: option
                                                    })}
                                                    className={`px-4 py-2 border-2 rounded-lg transition-all font-medium ${
                                                        selectedVariations[variation.name] === option
                                                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                                                            : 'border-amber-200 hover:border-amber-400 text-[#6B1028]'
                                                    }`}
                                                    data-testid={`variation-${variation.name}-${option}`}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Quantity */}
                        <div className="flex items-center gap-4 mb-8">
                            <label className="font-semibold text-[#6B1028]">Количество:</label>
                            <div className="flex items-center border-2 border-amber-200 rounded-lg">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="p-3 hover:bg-amber-50 transition-colors text-[#6B1028]"
                                    data-testid="qty-minus-btn"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-12 text-center font-bold text-[#6B1028]" data-testid="qty-value">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                                    className="p-3 hover:bg-amber-50 transition-colors text-[#6B1028]"
                                    data-testid="qty-plus-btn"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Add to Cart */}
                        <Button 
                            onClick={handleAddToCart}
                            disabled={product.stock === 0}
                            className="btn-gold w-full md:w-auto text-lg py-6 px-12 text-[#6B1028]"
                            data-testid="add-to-cart-btn"
                        >
                            <ShoppingCart className="h-5 w-5 mr-2" />
                            Добави в количката
                        </Button>

                        {/* Features */}
                        <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t-2 border-amber-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <Truck className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-sm text-[#6B1028]">Бърза доставка</p>
                                    <p className="text-xs text-[#6B1028]/60">С Еконт</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <Shield className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-sm text-[#6B1028]">Оригинален продукт</p>
                                    <p className="text-xs text-[#6B1028]/60">Гарантирано</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products - "Клиенти купиха също" */}
                {relatedProducts.length > 0 && (
                    <section className="mt-16 pt-12 border-t-2 border-amber-200" data-testid="related-products-section">
                        <div className="flex items-center gap-3 mb-8">
                            <Star className="h-6 w-6 text-amber-500" />
                            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#6B1028]">
                                Клиенти купиха също
                            </h2>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {relatedProducts.map((relProduct) => (
                                <ProductCard key={relProduct.product_id} product={relProduct} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ProductDetailPage;
